import produce from 'immer';

import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ParameterStructuredTable} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {
  DataParameterInfo,
  EquipmentParameterInfo,
  MaterialInputParameterInfo,
  MaterialOutputParameterInfo,
  ParameterInfo,
  ParameterStructuredTableMode,
  ProcedureParameterSchemaFieldName,
} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {
  StructuredTableSchema,
  StructuredTableSchemaMetadata,
} from 'notebook/structured-tables/StructuredTableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BioentityLinkSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {ParameterSettings} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';
import {
  ParameterTypedValue,
  ProcedureDataParameterSetting,
  ProcedureEquipmentSetting,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

const ASSERT_HAS_REST_FIELD_MESSAGE = 'Expected parameter to have a rest field representation';

/**
 * Extracts the value from a typed value to be used in the structured table.
 * Use this function to prevent validation errors when passing a typedValue to a string colum when submitting a structured table.
 */
function getParameterValue(
  typedValue: ParameterTypedValue
): BioentityLinkSchemaFieldTypedValue | string | null {
  if (typedValue == null) {
    return '';
  }
  if (typedValue.type === FieldType.BIOENTITY_LINK) {
    return typedValue;
  } else {
    return formatTypedValue(typedValue);
  }
}

export function assembleDataParameterInfos(
  dataParameterSettings: Array<ProcedureDataParameterSetting>,
  parameterConfirmationMethodsByParameterId: Map<string, ProcedureParameterConfirmationMethod>
): Array<DataParameterInfo> {
  // only allow inserting the parameter if we have the schema field representation of it available
  const dataParameterSettingsWithSchemaFields = dataParameterSettings.filter((dataParameter) =>
    Boolean(dataParameter.dataParameterVersion.restValueField)
  );
  if (dataParameterSettingsWithSchemaFields.length !== dataParameterSettings.length) {
    const dataParameterSettingsMissingSchemaFields = dataParameterSettings.filter(
      (dataParameter) => !dataParameterSettingsWithSchemaFields.includes(dataParameter)
    );
    logging.warn(ASSERT_HAS_REST_FIELD_MESSAGE, {extra: {dataParameterSettingsMissingSchemaFields}});
  }
  return dataParameterSettingsWithSchemaFields.map(({dataParameterVersion, values}): DataParameterInfo => {
    assert(
      dataParameterVersion.procedureDataParameterId != null,
      'Expected parent parameter to always exist when we are editing a step.'
    );
    assert(
      parameterConfirmationMethodsByParameterId.has(dataParameterVersion.procedureDataParameterId),
      `Expected parameter ID ${dataParameterVersion.procedureDataParameterId} to exist in parameter confirmation method mapping.`
    );
    const schemaFields: {[key: string]: SchemaField} = {};
    schemaFields[ProcedureParameterSchemaFieldName.VALUE_FIELD_NAME] = dataParameterVersion.restValueField!;
    return {
      name: dataParameterVersion.name,
      dataType: dataParameterVersion.dataType.type,
      versionId: dataParameterVersion.id!,
      paramId: dataParameterVersion.procedureDataParameterId,
      plannedValueId: values?.[0]?.id ?? null,
      schemaFields,
      value: values?.[0]?.value.value ?? null,
      unit: dataParameterVersion.dataType.unit?.symbol ?? '',
      minimum: dataParameterVersion.minimum?.value ?? null,
      maximum: dataParameterVersion.maximum?.value ?? null,
      description: dataParameterVersion.description,
      confirmationMethod: parameterConfirmationMethodsByParameterId.get(
        dataParameterVersion.procedureDataParameterId
      )!,
    };
  });
}

export function assembleMaterialInputParameterInfos(
  materialInputSettings: Array<ProcedureMaterialInputSetting>,
  parameterConfirmationMethodsByParameterId: Map<string, ProcedureParameterConfirmationMethod>
): Array<MaterialInputParameterInfo> {
  // only allow inserting the parameter if we have the schema field representation of it available
  const materialInputSettingsWithSchemaFields = materialInputSettings.filter((materialInput) =>
    Boolean(
      materialInput.materialInputVersion.restMaterialField &&
        materialInput.materialInputVersion.restAmountField
    )
  );
  if (materialInputSettingsWithSchemaFields.length !== materialInputSettings.length) {
    const materialInputSettingsMissingSchemaFields = materialInputSettings.filter(
      (materialInput) => !materialInputSettingsWithSchemaFields.includes(materialInput)
    );
    logging.warn(ASSERT_HAS_REST_FIELD_MESSAGE, {extra: {materialInputSettingsMissingSchemaFields}});
  }
  return materialInputSettingsWithSchemaFields.map(
    ({materialInputVersion, values}): MaterialInputParameterInfo => {
      assert(
        materialInputVersion.procedureMaterialInputId != null,
        'Expected parent parameter to always exist when we are editing a step.'
      );
      assert(
        parameterConfirmationMethodsByParameterId.has(materialInputVersion.procedureMaterialInputId),
        `Expected parameter ID ${materialInputVersion.procedureMaterialInputId} to exist in parameter confirmation method mapping.`
      );
      const schemaFields: {[key: string]: SchemaField} = {};
      schemaFields[ProcedureParameterSchemaFieldName.MATERIAL_FIELD_NAME] =
        materialInputVersion.restMaterialField!;
      schemaFields[ProcedureParameterSchemaFieldName.AMOUNT_FIELD_NAME] =
        materialInputVersion.restAmountField!;
      return {
        name: materialInputVersion.name,
        versionId: materialInputVersion.id!,
        dataType: materialInputVersion.dataType.type,
        paramId: materialInputVersion.procedureMaterialInputId,
        plannedValueId: values?.[0]?.id ?? null,
        schemaFields,
        plannedMaterial: getParameterValue(values?.[0]?.material),
        plannedAmount: values?.[0]?.amount?.value ?? null,
        // TODO: parent is not hooked up yet
        parent: '',
        description: materialInputVersion.description,
        confirmationMethod: parameterConfirmationMethodsByParameterId.get(
          materialInputVersion.procedureMaterialInputId
        )!,
      };
    }
  );
}

export function assembleMaterialOutputParameterInfos(
  materialOutputSettings: Array<ProcedureMaterialOutputSetting>
): Array<MaterialOutputParameterInfo> {
  // only allow inserting the parameter if we have the schema field representation of it available
  const materialOutputSettingsWithSchemaFields = materialOutputSettings.filter((materialOutput) =>
    Boolean(materialOutput.materialOutputVersion.restMaterialField)
  );
  if (materialOutputSettingsWithSchemaFields.length !== materialOutputSettings.length) {
    const materialOutputSettingsMissingSchemaFields = materialOutputSettings.filter(
      (materialOutput) => !materialOutputSettingsWithSchemaFields.includes(materialOutput)
    );
    logging.warn(ASSERT_HAS_REST_FIELD_MESSAGE, {extra: {materialOutputSettingsMissingSchemaFields}});
  }
  return materialOutputSettingsWithSchemaFields.map(
    ({materialOutputVersion}): MaterialOutputParameterInfo => {
      const schemaFields: {[key: string]: SchemaField} = {};
      schemaFields[ProcedureParameterSchemaFieldName.MATERIAL_FIELD_NAME] =
        materialOutputVersion.restMaterialField!;
      return {
        name: materialOutputVersion.name,
        dataType: materialOutputVersion.dataType.type,
        versionId: materialOutputVersion.id!,
        paramId: materialOutputVersion.procedureMaterialOutputId!,
        plannedValueId: null,
        schemaFields,
        description: materialOutputVersion.description,
        // Material Outputs can only be confirmed via Tables (for now)
        confirmationMethod: ProcedureParameterConfirmationMethod.TABLE,
      };
    }
  );
}

export function assembleEquipmentParameterInfos(
  equipmentSettings: Array<ProcedureEquipmentSetting>
): Array<EquipmentParameterInfo> {
  // only allow inserting the parameter if we have the schema field representation of it available
  const equipmentSettingsWithSchemaFields = equipmentSettings.filter((equipment) =>
    Boolean(equipment.equipmentVersion.restEquipmentField)
  );
  if (equipmentSettingsWithSchemaFields.length !== equipmentSettings.length) {
    const equipmentSettingsMissingSchemaFields = equipmentSettings.filter(
      (equipment) => !equipmentSettingsWithSchemaFields.includes(equipment)
    );
    logging.warn(ASSERT_HAS_REST_FIELD_MESSAGE, {extra: {equipmentSettingsMissingSchemaFields}});
  }
  return equipmentSettingsWithSchemaFields.map(({equipmentVersion, values}): EquipmentParameterInfo => {
    const schemaFields: {[key: string]: SchemaField} = {};
    schemaFields[ProcedureParameterSchemaFieldName.EQUIPMENT_FIELD_NAME] =
      equipmentVersion.restEquipmentField!;
    return {
      name: equipmentVersion.name,
      versionId: equipmentVersion.id!,
      paramId: equipmentVersion.procedureEquipmentId!,
      plannedValueId: values?.[0]?.id ?? null,
      schemaFields,
      description: equipmentVersion.description,
      plannedEquipment: getParameterValue(values?.[0]?.equipment),
      models: getParameterValue(equipmentVersion.models),
      dataType: equipmentVersion.dataType.type,
      // Equipment can only be confirmed via Tables (for now)
      confirmationMethod: ProcedureParameterConfirmationMethod.TABLE,
    };
  });
}

/** Convert ParameterSettings to ParameterInfos for a specific mode. The output of this does not discriminate
 * on Parameter type. */
export function assembleParameterInfos(
  parameterTableMode: ParameterStructuredTableMode,
  parameterSettings: ParameterSettings,
  parameterConfirmationMethodsByParameterId: Map<string, ProcedureParameterConfirmationMethod>
): Array<ParameterInfo> {
  switch (parameterTableMode) {
    case ParameterStructuredTableMode.PARAMETER_CONFIRMATION:
    case ParameterStructuredTableMode.PARAMETER_ACTUAL: {
      return assembleDataParameterInfos(
        parameterSettings.dataParameterSettings,
        parameterConfirmationMethodsByParameterId
      );
    }
    case ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION: {
      return assembleMaterialInputParameterInfos(
        parameterSettings.materialInputSettings,
        parameterConfirmationMethodsByParameterId
      );
    }
    case ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION: {
      return assembleMaterialOutputParameterInfos(parameterSettings.materialOutputSettings);
    }
    case ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION: {
      return assembleEquipmentParameterInfos(parameterSettings.equipmentSettings);
    }
    default:
      assertNever(parameterTableMode);
      return [];
  }
}

export function getFormattedDataParameterValue(dataParameter: DataParameterInfo): string {
  const unitStr = dataParameter.unit ?? '';
  const valueStr = dataParameter.value ?? '';

  if (valueStr.trim().length > 0 && unitStr.trim().length > 0) {
    // value and units are specified
    return `${valueStr.trim()} ${unitStr.trim()}`;
  } else {
    // value is specified without units, or is empty
    return valueStr.trim();
  }
}

export function assembleMinMaxString(pInfo: DataParameterInfo): string {
  const {minimum, maximum, unit} = pInfo;
  const minExists = minimum != null && minimum !== '';
  const maxExists = maximum != null && maximum !== '';

  let minMaxString = '';
  if (minExists && maxExists) {
    minMaxString = `${minimum} - ${maximum}`;
  } else if (minExists) {
    minMaxString = `≥ ${minimum}`;
  } else if (maxExists) {
    minMaxString = `≤ ${maximum}`;
  }

  return minMaxString && unit ? `${minMaxString} ${unit}` : minMaxString;
}

/** Convert ParameterInfos into schemas that are required for StructuredTables */
export function getTableSchemasFromParameterInfos(
  parameters: Array<ParameterInfo>
): Array<StructuredTableSchema> {
  return parameters.map((param) => {
    const fields = Object.values(param.schemaFields);
    const schema: StructuredTableSchema = {
      id: param.versionId,
      parentSchemaId: param.paramId,
      name: param.name,
      allFields: fields,
      fields,
    };
    return schema;
  });
}

/** Convert ParameterInfos into schema metadatas that are required for StructuredTables */
export function getTableSchemaMetadatasFromParameterInfos(
  parameters: Array<ParameterInfo>
): Array<StructuredTableSchemaMetadata> {
  return parameters.map(
    (p): StructuredTableSchemaMetadata => ({
      schemaId: p.versionId,
      parentSchemaId: p.paramId,
      group: null,
    })
  );
}

/**
 * Fix a Parameter Table's metadata to ensure it has the correct schemas. Many different codepaths can lead
 * to a Parameter Table having schema metadata that doesn't match the included rows, so this fixes any cases
 * by using the row metadata as the source of truth.
 */
export function autofixParameterTableSchemaMetadatas(
  parameterTable: ParameterStructuredTable
): ParameterStructuredTable {
  const usedSchemaIds = new Set(parameterTable.rows.map((row) => row.metadata.parameterVersionId));
  const filteredSchemaMetadatas = parameterTable.metadata.schemaMetadata.filter((schemaMetadata) =>
    usedSchemaIds.has(schemaMetadata.schemaId)
  );
  const filteredSchemas = parameterTable.schemas.filter((schema) => usedSchemaIds.has(schema.id));
  return produce(parameterTable, (draft) => {
    draft.schemas = filteredSchemas;
    draft.metadata.schemaMetadata = filteredSchemaMetadatas;
  });
}
